<template>
  <div class="parcelas-detalhadas animated fadeIn">
    <!-- Header -->
    <div class="parcelas-header mb-3 p-3">
      <div class="row align-items-center">
        <div class="col-md-6">
          <h5 class="mb-0">
            <i class="fas fa-list-ul text-primary"></i>
            Parcelas Detalhadas
          </h5>
          <small class="text-muted">
            {{ totalParcelas }} parcela(s) encontrada(s)
          </small>
        </div>
        <div class="col-md-6 text-right">
          <button
            @click="selecionarTodas"
            class="btn btn-sm btn-outline-secondary mr-2"
            :disabled="parcelas.length === 0">
            <i class="fas fa-check-square"></i>
            Selecionar Todas
          </button>
          <button
            @click="limparSelecao"
            class="btn btn-sm btn-outline-secondary mr-2"
            :disabled="parcelasSelecionadas.length === 0">
            <i class="fas fa-square"></i>
            Limpar
          </button>
          <button
            @click="criarPromessa"
            :disabled="parcelasSelecionadas.length === 0"
            class="btn btn-sm btn-success">
            <i class="fas fa-handshake"></i>
            Criar Promessa ({{ parcelasSelecionadas.length }})
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-center p-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Carregando parcelas...</p>
    </div>

    <!-- Sem parcelas -->
    <div v-else-if="!isLoading && parcelas.length === 0" class="text-center p-5">
      <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
      <p class="text-muted">Nenhuma parcela encontrada para este produto.</p>
    </div>

    <!-- Tabela de parcelas -->
    <div v-else class="table-responsive">
      <table class="table table-hover">
        <thead style="background-color: #04a791; color: white;">
          <tr>
            <th width="50" class="text-center" style="vertical-align: middle;">
              <input
                type="checkbox"
                :checked="todasSelecionadas"
                @change="toggleTodas">
            </th>
            <th style="vertical-align: middle;">Contrato</th>
            <th class="text-center" style="vertical-align: middle;">Parcela</th>
            <th style="vertical-align: middle;">Produto</th>
            <th style="vertical-align: middle;">Vencimento</th>
            <th class="text-center" style="vertical-align: middle;">Dias Atraso</th>
            <th class="text-right" style="vertical-align: middle;">Valor Parcela</th>
            <th class="text-right" style="vertical-align: middle;">Valor da Operação</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="parcela in parcelas"
            :key="parcela.id"
            :class="getRowClass(parcela)"
            @click="toggleParcela(parcela)">
            <td class="text-center" @click.stop>
              <input
                type="checkbox"
                v-model="parcela.selecionada"
                :disabled="parcela.temPromessaPendente"
                @change="onParcelaSelecionada">
            </td>
            <td>
              {{ parcela.contratoCredito }}
            </td>
            <td class="text-center">
              <div style="display: inline-flex; align-items: center; gap: 4px;">
                <span class="badge badge-info">
                  {{ parcela.numeroParcela }}
                </span>
              <span
                v-if="parcela.temPromessaPendente"
                :class="getBadgePromessaClass(parcela.statusPromessa)"
                v-b-tooltip.hover
                :title="getTooltipPromessa(parcela.statusPromessa)">
                <i class="fas fa-handshake"></i>
              </span>
              </div>
            </td>
            <td>
              {{ parcela.modalidadeProduto }}
            </td>
            <td>
              {{ formatDate(parcela.dataVencimento) }}
            </td>
            <td class="text-center">
              <span :class="getClassDiasAtraso(parcela.quantidadeDiasAtraso)">
                {{ parcela.quantidadeDiasAtraso }}
              </span>
            </td>
            <td class="text-right">
              {{ formatValue(parcela.valorParcela) }}
            </td>
            <td class="text-right" :class="getClassValorAtualizado(parcela)">
              {{ formatValue(parcela.valorAtualizado) }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="parcelasSelecionadas.length > 0" class="table-info">
          <tr>
            <td colspan="6" class="text-right">
              <strong>Total Selecionado:</strong>
            </td>
            <td class="text-right">
              <strong>{{ formatValue(valorTotalSelecionado) }}</strong>
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Modal de Promessa -->
    <ModalPromessaPagamento
      ref="modalPromessa"
      :parcelas-selecionadas="parcelasSelecionadas"
      :card-id="cardId"
      :board-id="boardId"
      :colunas-board="colunasBoard"
      @promessa-criada="onPromessaCriada"
    />
  </div>
</template>

<script>
import ParcelaRepository from '@/shared/http/repositories/parcela/ParcelaRepository'
import BoardColumnRepository from '@/shared/http/repositories/crm/boardcolumn'
import ModalPromessaPagamento from './ModalPromessaPagamento.vue'
import Tools from '@/components/tools/Tools.js'
import { toast } from '@/shared/utils/UIHelper'
import { PromessaEventBus } from '@/components/events/promessaEventBus'

export default {
  name: 'ParcelasDetalhadas',
  components: {
    ModalPromessaPagamento
  },
  props: {
    cardProdutos: {
      type: Array,
      required: true
    },
    cardId: {
      type: Number,
      required: true
    },
    card: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data () {
    return {
      parcelas: [],
      isLoading: false,
      colunas: []
    }
  },
  computed: {
    totalParcelas () {
      return this.parcelas.length
    },

    parcelasSelecionadas () {
      return this.parcelas.filter(p => p.selecionada)
    },

    todasSelecionadas () {
      // Considera apenas parcelas sem promessa ativa
      const parcelasSemPromessa = this.parcelas.filter(p => !p.temPromessaPendente)
      return parcelasSemPromessa.length > 0 &&
             parcelasSemPromessa.every(p => p.selecionada)
    },

    valorTotalSelecionado () {
      return this.parcelasSelecionadas.reduce((total, parcela) => {
        // O total selecionado deve representar o VALOR DA PARCELA (planilha: vlrATUALIZADO)
        // e não o valor total da operação.
        return total + (parcela.valorParcela || 0)
      }, 0)
    },

    boardId () {
      if (this.card && this.card.boardColumn && this.card.boardColumn.board) {
        return this.card.boardColumn.board.id
      }
      return null
    },

    colunasBoard () {
      return this.colunas
    }
  },
  mounted () {
    this.carregarParcelas()
    this.carregarColunasBoard()

    // Escutar evento global de atualização de promessas
    PromessaEventBus.$on('promessa-atualizada', this.recarregarParcelas)
  },
  beforeDestroy () {
    // Limpar listener ao destruir componente
    PromessaEventBus.$off('promessa-atualizada', this.recarregarParcelas)
  },
  watch: {
    cardProdutos () {
      this.carregarParcelas()
    },
    boardId () {
      this.carregarColunasBoard()
    }
  },
  methods: {
    async carregarParcelas () {
      if (!this.cardProdutos || this.cardProdutos.length === 0) {
        this.parcelas = []
        return
      }

      this.isLoading = true

      try {
        // Busca parcelas de todos os CardProdutos
        const promises = this.cardProdutos.map(cardProduto =>
          ParcelaRepository.getParcelasEmAtrasoByCardProduto(cardProduto.id)
        )

        const responses = await Promise.all(promises)

        // Combina todas as parcelas em um array único
        // O backend já retorna temPromessa e statusPromessa corretos
        this.parcelas = responses.flatMap(response => {
          const parcelas = response.data || []
          // Mapear temPromessa para temPromessaPendente
          return parcelas.map(p => ({
            ...p,
            temPromessaPendente: p.temPromessa || false,
            selecionada: false
          }))
        })
      } catch (error) {
        console.error('Erro ao carregar parcelas:', error)
        toast.error('Erro ao carregar parcelas detalhadas')
      } finally {
        this.isLoading = false
      }
    },

    async carregarColunasBoard () {
      if (!this.boardId) {
        this.colunas = []
        return
      }

      try {
        const response = await BoardColumnRepository.SearchActive(this.boardId, 100, 0)
        this.colunas = response.data.content || []
      } catch (error) {
        console.error('Erro ao carregar colunas do board:', error)
        this.colunas = []
      }
    },

    selecionarTodas () {
      this.parcelas.forEach(parcela => {
        // Só seleciona se NÃO tiver promessa ativa
        if (!parcela.temPromessaPendente) {
          parcela.selecionada = true
        }
      })
    },

    limparSelecao () {
      this.parcelas.forEach(parcela => {
        parcela.selecionada = false
      })
    },

    toggleTodas () {
      const todasSelecionadas = this.todasSelecionadas
      this.parcelas.forEach(parcela => {
        // Só altera seleção se NÃO tiver promessa ativa
        if (!parcela.temPromessaPendente) {
          parcela.selecionada = !todasSelecionadas
        }
      })
    },

    toggleParcela (parcela) {
      // Não permite selecionar parcelas que já têm promessa pendente
      if (parcela.temPromessaPendente) {
        return
      }
      parcela.selecionada = !parcela.selecionada
    },

    onParcelaSelecionada () {
      // Force update para recalcular computed
      this.$forceUpdate()
    },

    criarPromessa () {
      if (this.parcelasSelecionadas.length === 0) {
        toast.warning('Selecione pelo menos uma parcela')
        return
      }

      this.$refs.modalPromessa.showModal()
    },

    onPromessaCriada () {
      // Recarregar parcelas para atualizar status
      this.carregarParcelas()
      this.$emit('promessa-criada')
    },

    recarregarParcelas () {
      // Método público para ser chamado externamente
      this.carregarParcelas()
    },

    getRowClass (parcela) {
      if (parcela.temPromessaPendente) {
        return 'row-disabled'
      }
      if (parcela.selecionada) {
        return 'table-active'
      }
      return ''
    },

    getClassDiasAtraso (dias) {
      if (dias > 90) {
        return 'badge badge-danger'
      } else if (dias > 30) {
        return 'badge badge-warning'
      } else {
        return 'badge badge-info'
      }
    },

    getClassValorAtualizado (parcela) {
      if (parcela.valorAtualizado > parcela.valorParcela) {
        return 'text-danger'
      }
      return ''
    },

    getClassSituacao (situacao) {
      const classes = {
        'EM_ATRASO': 'badge-danger',
        'VENCENDO_HOJE': 'badge-warning',
        'FUTURA': 'badge-success'
      }
      return classes[situacao] || 'badge-secondary'
    },

    formatValue (value) {
      return Tools.formatMoneyBR(value)
    },

    formatDate (date) {
      if (!date) return '-'

      // Se a data vem como string
      if (typeof date === 'string') {
        // Extrair apenas a parte da data (antes do T ou espaço)
        const datePart = date.split('T')[0].split(' ')[0]

        // Verificar se está no formato yyyy-MM-dd
        if (datePart.includes('-')) {
          const [year, month, day] = datePart.split('-')
          return `${day}/${month}/${year}`
        }
      }

      // Fallback para objeto Date
      const d = new Date(date)
      return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    },

    getBadgePromessaClass (status) {
      const classes = {
        'PENDENTE': 'badge badge-info',
        'QUEBRADO': 'badge badge-danger',
        'CUMPRIDO': 'badge badge-success'
      }
      return classes[status] || 'badge badge-warning'
    },

    getTooltipPromessa (status) {
      const tooltips = {
        'PENDENTE': 'Promessa pendente - não pode criar nova',
        'QUEBRADO': 'Promessa quebrada - não pode criar nova',
        'CUMPRIDO': 'Promessa cumprida - parcela liquidada'
      }
      return tooltips[status] || 'Possui promessa'
    }
  }
}
</script>

<style scoped>
.parcelas-detalhadas {
  .parcelas-header {
    background: #f8f9fa;
    border-radius: 0.375rem;
    border: 1px solid #dee2e6;
  }

  .table {
    th {
      font-weight: bold;
      font-size: 12px;
    }

    td {
      font-size: 11px;
      font-weight: normal;
      vertical-align: middle;
    }

    tbody tr {
      cursor: pointer;

      &:hover {
        background-color: #f8f9fa;
      }

      &.table-active {
        background-color: #e3f2fd !important;
      }

      &.row-disabled {
        background-color: #f8f9fa;
        opacity: 0.6;
        cursor: not-allowed;

        input[type="checkbox"] {
          cursor: not-allowed;
        }
      }
    }
  }

  .badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/private/crm/details/product/components/ParcelasDetalhadas.vue